import {HttpClient, HttpContext, HttpParams, HttpResponse} from '@angular/common/http';
import {Observable, tap} from 'rxjs';
import {SUPPRESS_ERROR_HANDLER} from 'features/core/shared/service/crud/suppress-error-token';

/**
 * Extracts the file name of a `Content-Disposition` header (`filename*=UTF-8''…` wins over
 * `filename="…"`). Returns null when the header is missing or carries no name — the header may also be
 * invisible to the browser when the server does not expose it, so callers always pass a fallback.
 */
export function cpFileNameFromContentDisposition(header: string | null | undefined): string | null {
    if (!header) {
        return null;
    }
    const encoded = /filename\*\s*=\s*(?:UTF-8|utf-8)''([^;]+)/.exec(header);
    if (encoded) {
        try {
            return decodeURIComponent(encoded[1].trim());
        } catch {
            // fall through to the plain form
        }
    }
    const plain = /filename\s*=\s*"?([^";]+)"?/.exec(header);
    return plain ? plain[1].trim() : null;
}

/**
 * Fetches an authenticated file answer as a blob and hands it to the browser to save, using the
 * file name of the server's `Content-Disposition` header (fallback when absent). Same anchor
 * mechanics as `CpProvisionRunService#downloadFile$`; the Observable is returned so the CALLER owns
 * the busy state and error handling. `suppressGlobalErrorToast` keeps the global HTTP error toast away
 * when the caller shows the server's German sentence itself.
 */
export function cpDownloadBlobWithServerName$(http: HttpClient,
                                              method: 'GET' | 'POST',
                                              url: string,
                                              params: HttpParams,
                                              fallbackFileName: string,
                                              suppressGlobalErrorToast: boolean = false): Observable<HttpResponse<Blob>> {
    const context = new HttpContext().set(SUPPRESS_ERROR_HANDLER, suppressGlobalErrorToast);
    const request$ = method === 'GET'
        ? http.get(url, {params, context, responseType: 'blob', observe: 'response'})
        : http.post(url, {}, {params, context, responseType: 'blob', observe: 'response'});
    return request$.pipe(
        tap((response: HttpResponse<Blob>) => {
            if (!response.body) {
                return;
            }
            const fileName = cpFileNameFromContentDisposition(response.headers.get('Content-Disposition'))
                ?? fallbackFileName;
            const downloadUrl = window.URL.createObjectURL(response.body);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = fileName;
            link.click();
            window.URL.revokeObjectURL(downloadUrl);
        })
    );
}
